import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { LuMail } from 'react-icons/lu';
import { useNavigate } from 'react-router';
import { ResetUserPasswordToken } from './compiled_proto/com/celertech/baseserver/api/user/DownstreamAuthenticationUserProto';
import { Button } from './components/common/Button';
import StatusIndicator from './components/common/StatusIndicator';
import { forgotPasswordSchema } from './components/form/schema/forgotPasswordSchema';
import RHFInput from './components/inputs/RHFInput';
import language from './config/language';
import { isWolfBTCPlatform } from './helpers/environmentHelper';
import { LoginFooter, LoginLogo } from './Login';
import { logToServer } from './services/LogService';
import { sendResetPasswordEmail } from './services/UserService';
import { useAppSelector } from './state/hooks';
import { selectLanguage } from './state/reducers/languageSlice';
import useToast from './utils/hooks/useToast';

interface ForgotPasswordFormValues {
    username: string;
    server: unknown;
}

function ForgotPassword() {
    const lang = useAppSelector(selectLanguage);

    const navigate = useNavigate();
    const [toastError, toastSuccess] = useToast();

    const [retryTimer, setRetryTimer] = useState<number>(0);
    const [resetPasswordResponse, setResetPasswordResponse] = useState<ResetUserPasswordToken>();
    const form = useForm<ForgotPasswordFormValues>({
        defaultValues: {
            username: ''
        },
        mode: 'onChange',
        resolver: yupResolver(forgotPasswordSchema)
    });
    const {
        handleSubmit,
        setError,
        formState: { errors, isSubmitted, isSubmitting },
        register
    } = form;

    const onSubmit = async (data: ForgotPasswordFormValues) => {
        try {
            const user = await sendResetPasswordEmail(data.username);
            logToServer('info', `User [${data.username}] has requested password reset email.`);
            setResetPasswordResponse(user);
            setRetryTimer(30);
        } catch (error: any) {
            // handle error here
            const msg = 'Something went wrong during password reset request.';
            logToServer(
                'info',
                `User [${data.username}] has trouble requesting password reset email. ${error?.message}`
            );
            setError('server', { type: 'custom', message: msg });
            toastError({ body: msg, title: 'Reset password failure', persist: true });
        }
    };

    useEffect(() => {
        if (resetPasswordResponse) {
            const timer = setTimeout(() => setRetryTimer(retryTimer - 1), 1000);
            if (retryTimer < 1) {
                clearTimeout(timer);
                setResetPasswordResponse(undefined);
            }
        }
    }, [resetPasswordResponse, retryTimer]);

    return (
        <div className="h-full flex flex-col justify-center py-12 px-6 bg-brand-background">
            <LoginLogo />
            {/* <div className="absolute top-6 right-6 flex items-center gap-4">
                <ThemeButton className="w-4 h-4" />
                <LanguageButton className="w-4 h-4" />
            </div> */}
            {resetPasswordResponse ? (
                <div className="flex flex-col items-center justify-center p-4 relative">
                    <div className="w-full max-w-md relative z-10">
                        <div className="relative my-6 mx-auto w-full max-w-md">
                            <div className="p-4 sm:p-6 text-center rounded-md sm:rounded-lg sm:px-8 bg-brand-background brand-shadow z-0">
                                <div className="w-16 h-16 bg-brand-neutral-700 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <LuMail className="text-brand-primary-light" size={32} strokeWidth={1.5} />
                                </div>
                                <h1 className="text-2xl font-semibold text-brand-text mb-2 tracking-tight">
                                    {language[lang].resetPassword.checkYourEmail}
                                </h1>
                                <p className="text-brand-neutral-400 text-sm mb-8 leading-relaxed px-2">
                                    {language[lang].resetPassword.weSentPasswordResetLink}
                                </p>
                                <div className="space-y-4">
                                    <p className="text-sm text-brand-neutral-400">
                                        {language[lang].resetPassword.didntReceiveEmail}{' '}
                                        <button
                                            onClick={() => setResetPasswordResponse(undefined)}
                                            className="text-brand-text font-medium hover:underline focus:outline-none">
                                            {language[lang].resetPassword.clickHereToResend}
                                        </button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <FormProvider {...form}>
                    <form className="relative" onSubmit={handleSubmit(onSubmit)}>
                        <div className="relative my-6 mx-auto w-full max-w-md">
                            <div className="p-4 sm:p-6 rounded-md sm:rounded-lg sm:px-8 bg-brand-background brand-shadow z-0">
                                <div className="absolute top-4 right-4">
                                    <StatusIndicator showLabel={false} size="md" />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <div className="flex gap-1 items-center justify-center text-center text-sm mb-4">
                                        <p className="text-brand-text">{language[lang].forgotPassword.title}</p>
                                    </div>
                                    <RHFInput
                                        {...register('username')}
                                        label={language[lang].forgotPassword.username}
                                        autoComplete="username"
                                    />
                                    {errors['server'] && isSubmitted ? (
                                        <span className="text-brand-red text-xs">{errors['server'].message}</span>
                                    ) : null}
                                    <Button isLoading={isSubmitting}>
                                        {language[lang].forgotPassword.resetButton}
                                    </Button>
                                </div>
                                <div
                                    className="mt-6 relative flex justify-center text-sm font-medium text-brand-neutral-300 hover:text-brand-text cursor-pointer"
                                    onClick={() => navigate('/login')}>
                                    {language[lang].forgotPassword.backToLogin}
                                </div>
                            </div>
                        </div>
                        {!isWolfBTCPlatform && <LoginFooter />}
                    </form>
                </FormProvider>
            )}
        </div>
    );
}

export default ForgotPassword;
